import assetUtils, { Asset, getItokenAddressInfo } from 'app-lib/assetUtils'
import providerUtils, { AnyProvider } from 'app-lib/providerUtils'
import { BigNumber } from '@0x/utils'
import {
  chainConfigs,
  ChainId,
  isChainSupported,
  stakingConfig,
  supportedChains,
} from '../config/appConfig'
import ReserveDetails from '../domain/fulcrum/ReserveDetails'
import oracleApi from './apis/oracleApi'
import bzxApi, { IAllTokensStats, IChainStat } from './apis/bzxApi'
import subgraphHistoryUtils, { ActivityEvent } from './subgraphHistoryUtils'
import {
  BorrowEvent,
  BurnEvent,
  CloseWithDepositEvent,
  CloseWithSwapEvent,
  DepositCollateralEvent,
  LiquidationEvent,
  MintEvent,
  TradeEvent,
  WithdrawCollateralEvent,
} from '../domain/events'
import tradeUtils from './tradeUtils'
import StakeEvent from '../domain/events/blockchain/StakeEvent'
import UnstakeEvent from '../domain/events/blockchain/UnstakeEvent'
import { IActivityInfo, Operation } from '../domain/txTypes'
import { switchNetwork } from './web3ReactUtils'

/**
 * Calculate how many USDT, USDC, DAI are staked as 3CRV token in Staking Dashboard
 */
const getStakedTokensFromCRV = async (
  provider: AnyProvider,
  daiReserveDetails: ReserveDetails,
  usdcReserveDetails: ReserveDetails,
  usdtReserveDetails: ReserveDetails
): Promise<{
  daiReserve: { lockedAssets: BigNumber; usdTotalLocked: BigNumber }
  usdcReserve: { lockedAssets: BigNumber; usdTotalLocked: BigNumber }
  usdtReserve: { lockedAssets: BigNumber; usdTotalLocked: BigNumber }
}> => {
  const result: {
    daiReserve: { lockedAssets: BigNumber; usdTotalLocked: BigNumber }
    usdcReserve: { lockedAssets: BigNumber; usdTotalLocked: BigNumber }
    usdtReserve: { lockedAssets: BigNumber; usdTotalLocked: BigNumber }
  } = {
    daiReserve: { lockedAssets: new BigNumber(0), usdTotalLocked: new BigNumber(0) },
    usdcReserve: { lockedAssets: new BigNumber(0), usdTotalLocked: new BigNumber(0) },
    usdtReserve: { lockedAssets: new BigNumber(0), usdTotalLocked: new BigNumber(0) },
  }
  if (!provider.contractsSource || !isChainSupported(provider.currentChainId)) {
    return result
  }
  const stakingV1Address = stakingConfig.stakingContract
  const crvAddress = assetUtils.getAddress(provider.currentChainId, Asset.CRV)
  if (crvAddress) {
    // 3CRV swap contract
    const threePoolContract = await provider.contractsSource.getThreePoolContract()
    const crvErc20Contract = provider.contractsSource.getErc20Contract(crvAddress)

    const crvStakedLockedAmount = await crvErc20Contract.balanceOf(stakingV1Address).callAsync()
    const crvTotalSupply = await crvErc20Contract.totalSupply().callAsync()

    // get a share of 3CRV tokens in staking contract compare to all exisiting 3CRV tokens
    const shareOfCrvLockedInStaking = crvStakedLockedAmount.div(crvTotalSupply)

    if (daiReserveDetails && daiReserveDetails.decimals && daiReserveDetails.swapToUSDPrice) {
      const precision = new BigNumber(10 ** (18 - daiReserveDetails.decimals))
      // underlying DAI in 3CRV
      const underlyingDaiInCRV = await threePoolContract.balances(new BigNumber(0)).callAsync()
      const shareOfDaiInStakedCrv = underlyingDaiInCRV
        .times(precision)
        .times(shareOfCrvLockedInStaking)

      result.daiReserve.lockedAssets = shareOfDaiInStakedCrv.div(10 ** 18)

      const stakingDaiUsdLocked = shareOfDaiInStakedCrv
        .times(daiReserveDetails.swapToUSDPrice.dividedBy(10 ** 18))
        .div(10 ** 18)
      result.daiReserve.usdTotalLocked = stakingDaiUsdLocked
    }

    if (usdtReserveDetails && usdtReserveDetails.decimals && usdtReserveDetails.swapToUSDPrice) {
      const precision = new BigNumber(10 ** (18 - usdtReserveDetails.decimals))
      // underlying USDT in 3CRV
      const underlyingUsdtInCRV = await threePoolContract.balances(new BigNumber(2)).callAsync()
      const shareOfUsdtInStakedCrv = underlyingUsdtInCRV
        .times(precision)
        .times(shareOfCrvLockedInStaking)
      result.usdtReserve.lockedAssets = shareOfUsdtInStakedCrv.div(10 ** 18)

      const stakingUsdtUsdLocked = shareOfUsdtInStakedCrv
        .times(usdtReserveDetails.swapToUSDPrice.dividedBy(10 ** 18))
        .div(10 ** 18)

      result.usdtReserve.usdTotalLocked = stakingUsdtUsdLocked
    }

    if (usdcReserveDetails && usdcReserveDetails.decimals && usdcReserveDetails.swapToUSDPrice) {
      const precision = new BigNumber(10 ** (18 - usdcReserveDetails.decimals))
      // underlying USDC in 3CRV
      const underlyingUsdcInCRV = await threePoolContract.balances(new BigNumber(1)).callAsync()
      const shareOfUsdcInStakedCrv = underlyingUsdcInCRV
        .times(precision)
        .times(shareOfCrvLockedInStaking)

      result.usdtReserve.lockedAssets = shareOfUsdcInStakedCrv.div(10 ** 18)

      const stakingUsdcUsdLocked = shareOfUsdcInStakedCrv
        .times(usdcReserveDetails.swapToUSDPrice.dividedBy(10 ** 18))
        .div(10 ** 18)

      result.usdcReserve.usdTotalLocked = stakingUsdcUsdLocked
    }
  }
  return result
}

/**
 * Get reserve of tokens like stMATIC, wstETH
 * Use only for this type of tokens; they do not have iTokens and were added separately
 */
async function getChainStakedTokenReserve(
  provider: AnyProvider,
  token: Asset
): Promise<ReserveDetails> {
  const { chainId } = providerUtils.getDependenciesStrictRead(provider)
  const chain = chainConfigs[chainId]

  const tokenInfo = assetUtils.getInfo(chainId, token)
  const balance = (
    await providerUtils.getErc20Balance(provider, tokenInfo.address, chain.contracts.bzx)
  ).div(10 ** tokenInfo.decimals)

  const swapRate = await oracleApi.getSwapToUsdRate(provider, token)

  const reserve = new ReserveDetails(
    tokenInfo.asset,
    tokenInfo.address,
    tokenInfo.asset,
    tokenInfo.displayName,
    tokenInfo.decimals,
    null,
    new BigNumber(0),
    new BigNumber(0),
    new BigNumber(0),
    new BigNumber(0),
    new BigNumber(0),
    new BigNumber(0),
    new BigNumber(0),
    new BigNumber(0),
    balance,
    swapRate,
    new BigNumber(0),
    swapRate.times(balance)
  )
  return reserve
}

const getReserveDetails = async (
  provider: AnyProvider,
  assets: Asset[]
): Promise<{ allTokensStats: IAllTokensStats; stats: ReserveDetails[] }> => {
  const { helperContract, chainId } = providerUtils.getDependenciesStrictRead(provider)
  const tokens = assetUtils.getITokenAddresses(chainId, assets)

  let swapRates
  try {
    swapRates = (
      await oracleApi.getSwapToUsdRateBatch(
        provider,
        assets,
        chainConfigs[provider.currentChainId].stablecoinForUsdRates
      )
    )[0]
  } catch (e) {
    console.error(e)
  }

  const reserveData = await helperContract.reserveDetails(tokens).callAsync()
  let usdSupplyAll = new BigNumber(0)
  let usdTotalLockedAll = new BigNumber(0)
  const stats: ReserveDetails[] = []

  for (let i = 0; i < reserveData.length; i++) {
    const reserve = reserveData[i]
    const asset = assetUtils.getUnderlyingAssetFromItokenAddress(chainId, reserve.iToken)
    const symbol = ''
    const name = ''
    const assetAddress: string = tokens[i]

    let totalAssetSupply = new BigNumber(reserve.totalAssetSupply)
    let totalAssetBorrow = new BigNumber(reserve.totalAssetBorrow)
    const supplyInterestRate = new BigNumber(reserve.supplyInterestRate)
    const borrowInterestRate = new BigNumber(reserve.borrowInterestRate)
    const torqueBorrowInterestRate = new BigNumber(reserve.torqueBorrowInterestRate)
    let vaultBalance = new BigNumber(reserve.vaultBalance)
    let marketLiquidity = totalAssetSupply.minus(totalAssetBorrow)

    const decimals = assetUtils.getDecimals(chainId, asset)
    let usdSupply = new BigNumber(0)
    let usdTotalLocked = new BigNumber(0)

    const precision = new BigNumber(10 ** (18 - decimals))
    totalAssetSupply = totalAssetSupply.times(precision)
    totalAssetBorrow = totalAssetBorrow.times(precision)
    marketLiquidity = marketLiquidity.times(precision)
    //liquidityReserved = liquidityReserved.times(precision);
    vaultBalance = vaultBalance.times(precision)

    if (swapRates && swapRates[i]) {
      usdSupply = totalAssetSupply.times(swapRates[i]).dividedBy(10 ** 18)
      usdSupplyAll = usdSupplyAll.plus(usdSupply)

      usdTotalLocked = marketLiquidity
        .plus(vaultBalance)
        .times(swapRates[i])
        .dividedBy(10 ** 18)
      usdTotalLockedAll = usdTotalLockedAll.plus(usdTotalLocked)
    }

    stats.push(
      new ReserveDetails(
        asset,
        assetAddress,
        symbol,
        name,
        decimals,
        null, // tokenPrice.dividedBy(10 ** 18),
        marketLiquidity.dividedBy(10 ** 18),
        // liquidityReserved.dividedBy(10 ** 18),
        new BigNumber(0),
        totalAssetSupply.dividedBy(10 ** 18),
        totalAssetBorrow.dividedBy(10 ** 18),
        supplyInterestRate.dividedBy(10 ** 18),
        borrowInterestRate.dividedBy(10 ** 18),
        torqueBorrowInterestRate.dividedBy(10 ** 18),
        // avgBorrowInterestRate.dividedBy(10 ** 18),
        new BigNumber(0),
        vaultBalance.dividedBy(10 ** 18),
        swapRates ? swapRates[i] : new BigNumber(0),
        usdSupply.dividedBy(10 ** 18),
        usdTotalLocked.dividedBy(10 ** 18)
      )
    )
  }

  if (chainId === 137) {
    // Because stMATIC has no itoken and wont be in reserves, need to query separately
    const stMaticReserve = await getChainStakedTokenReserve(provider, Asset.STMATIC)
    usdTotalLockedAll = usdTotalLockedAll.plus(stMaticReserve.usdTotalLocked?.times(10 ** 18) || 0)
    stats.push(stMaticReserve)
  } else if (chainId === 1) {
    // Because wstETH has no itoken and wont be in reserves, need to query separately
    const wstEthReserve = await getChainStakedTokenReserve(provider, Asset.WSTETH)
    usdTotalLockedAll = usdTotalLockedAll.plus(wstEthReserve.usdTotalLocked?.times(10 ** 18) || 0)
    stats.push(wstEthReserve)
  }

  usdSupplyAll = usdSupplyAll.div(10 ** 18)
  usdTotalLockedAll = usdTotalLockedAll.div(10 ** 18)

  if (chainId === 1) {
    /**
     * Add USDT, USDC, DAI from staked CRV to protocol stats
     */
    const daiReserveDetails = stats.find((e) => e.asset === Asset.DAI)
    const usdcReserveDetails = stats.find((e) => e.asset === Asset.USDC)
    const usdtReserveDetails = stats.find((e) => e.asset === Asset.USDT)
    if (daiReserveDetails && usdtReserveDetails && usdcReserveDetails) {
      // TODO: should check if this whole code related to CRV still makes sense; add explanation or delete
      const { daiReserve, usdcReserve, usdtReserve } = await getStakedTokensFromCRV(
        provider,
        daiReserveDetails,
        usdcReserveDetails,
        usdtReserveDetails
      )
      daiReserveDetails.lockedAssets = daiReserveDetails.lockedAssets
        ? daiReserveDetails.lockedAssets.plus(daiReserve.lockedAssets)
        : daiReserve.lockedAssets
      usdcReserveDetails.lockedAssets = usdcReserveDetails.lockedAssets
        ? usdcReserveDetails.lockedAssets.plus(usdcReserve.lockedAssets)
        : usdcReserve.lockedAssets
      usdtReserveDetails.lockedAssets = usdtReserveDetails.lockedAssets
        ? usdtReserveDetails.lockedAssets.plus(usdtReserve.lockedAssets)
        : usdtReserve.lockedAssets

      daiReserveDetails.usdTotalLocked = daiReserveDetails.usdTotalLocked
        ? daiReserveDetails.usdTotalLocked.plus(daiReserve.usdTotalLocked)
        : daiReserve.usdTotalLocked
      usdcReserveDetails.usdTotalLocked = usdcReserveDetails.usdTotalLocked
        ? usdcReserveDetails.usdTotalLocked.plus(usdcReserve.usdTotalLocked)
        : usdcReserve.usdTotalLocked
      usdtReserveDetails.usdTotalLocked = usdtReserveDetails.usdTotalLocked
        ? usdtReserveDetails.usdTotalLocked.plus(usdtReserve.usdTotalLocked)
        : usdtReserve.usdTotalLocked

      usdTotalLockedAll = usdTotalLockedAll
        .plus(daiReserve.usdTotalLocked)
        .plus(usdcReserve.usdTotalLocked)
        .plus(usdtReserve.usdTotalLocked)
    }
  }

  const allTokensStats = {
    usdSupply: usdSupplyAll.toFixed(),
    usdTotalLocked: usdTotalLockedAll.toFixed(),
  }

  return { stats, allTokensStats }
}

/**
 * Uses reserveDetails to provide chain stats with the same format than the backend api
 */
async function getStatsOnChain(provider: AnyProvider): Promise<IChainStat[]> {
  const { chainId } = providerUtils.getDependenciesStrictRead(provider)
  const chain = chainConfigs[chainId]
  const lendingAssets = chain.lending.assets
  const { stats, allTokensStats } = await getReserveDetails(provider, lendingAssets)
  const chainStats: IChainStat = { chainId, stats: [], allTokensStats }

  chainStats.stats = stats.map((item) => {
    // @see bzxApi.getChainStats for why we rename asset
    const tokenName =
      chain.native.asset === item.asset
        ? chain.native.wrapped
        : item.asset === 'ETH' && chainId === 137
        ? Asset.WETH
        : item.asset
    const decimals = item.decimals || 18
    const { iTokenAddress, iTokenExplorerUrl } = getItokenAddressInfo(chainId, tokenName)
    const assetsCommon = assetUtils.getCommon(tokenName)
    return {
      address: item.addressErc20,
      availableLiquidity: item.liquidity ? item.liquidity.toFixed(decimals) : 'NaN',
      borrowApr: item.borrowInterestRate ? item.borrowInterestRate.toFixed(decimals) : 'NaN',
      supplyApr: item.supplyInterestRate ? item.supplyInterestRate.toFixed(decimals) : 'NaN',
      tokenName,
      tokenUsdPrice: item.swapToUSDPrice ? item.swapToUSDPrice.toFixed(2) : 'NaN',
      totalBorrow: item.totalBorrow ? item.totalBorrow.toFixed(decimals) : 'NaN',
      totalSupply: item.totalSupply ? item.totalSupply.toFixed(decimals) : 'NaN',
      usdTotalLocked: item.usdTotalLocked ? item.usdTotalLocked.toFixed(2) : 'NaN',
      usdSupply: item.usdSupply ? item.usdSupply.toFixed(2) : 'NaN',
      vaultBalance: item.lockedAssets ? item.lockedAssets.toFixed(decimals) : 'NaN',
      decimals,
      iTokenAddress,
      iTokenExplorerUrl,
      logo: assetsCommon ? assetsCommon.spriteLogo : '',
    }
  })
  return [chainStats]
}

/**
 * Get data from the backend api first by default, fallback to blockchain in case of failure
 */
async function getChainStats(
  provider: AnyProvider,
  chainId?: ChainId,
  opts?: { onChain: boolean }
): Promise<IChainStat[]> {
  if (opts?.onChain) {
    return getStatsOnChain(provider)
  }
  try {
    const fromApi = await bzxApi.getChainStats(chainId)
    return fromApi
  } catch (err) {
    console.error('getChainStats Backend API failed.', 'Trying from blockchain...', err)
    // when there is fallback to the blockchain, we only query from the current chain
    return getStatsOnChain(provider)
  }
}

function getTradeEventInfo(e: TradeEvent | CloseWithSwapEvent) {
  const { isLong, positionType } = tradeUtils.guessLongShort(
    e.chainId,
    e.loanToken,
    e.collateralToken
  )
  let asset = isLong ? e.collateralToken : e.loanToken
  let leverage = new BigNumber(e.currentLeverage.div(10 ** 18))

  let type = 'Closed ' + positionType
  let value = new BigNumber(0)

  if (e instanceof TradeEvent) {
    asset = e.collateralToken
    const { decimals } = assetUtils.getChainDetails(e.chainId, asset)
    value = e.positionSize.div(10 ** decimals)
    if (isLong) {
      leverage = leverage.plus(1)
      type = 'Longed'
    } else {
      type = 'Shorted'
    }
  } else {
    const { decimals } = assetUtils.getChainDetails(e.chainId, asset)
    const closeAmount = isLong ? e.positionCloseSize : e.loanCloseAmount
    value = closeAmount.div(10 ** decimals)
  }

  return {
    asset,
    pair: isLong ? e.collateralToken + '/' + e.loanToken : e.loanToken + '/' + e.collateralToken,
    type,
    operation: Operation.Trade,
    leverage,
    value,
  }
}

function getBorrowEventInfo(e: BorrowEvent | CloseWithDepositEvent) {
  const asset = e.loanToken
  const { decimals } = assetUtils.getChainDetails(e.chainId, asset)
  if (e instanceof BorrowEvent) {
    return {
      asset,
      operation: Operation.Borrow,
      type: 'borrowed',
      value: e.newPrincipal.div(10 ** decimals),
      collateral: e.collateralToken,
    }
  }
  return {
    asset,
    operation: Operation.Borrow,
    type: 'closed borrow',
    value: e.repayAmount.div(10 ** decimals),
  }
}

function getLendEventInfo(e: MintEvent | BurnEvent) {
  const asset = assetUtils.getAssetByIToken(e.chainId, e.asset)
  const { decimals } = assetUtils.getChainDetails(e.chainId, asset)
  const value = e.assetAmount.div(10 ** decimals)

  return {
    asset,
    operation: Operation.Lend,
    type: e instanceof MintEvent ? 'lent' : 'unlent',
    value,
  }
}

function getLiquidationEventInfo(e: LiquidationEvent) {
  const { asset } = e
  const { decimals } = assetUtils.getChainDetails(e.chainId, asset)
  const value = e.repayAmount.div(10 ** decimals)

  return {
    asset,
    operation: Operation.Trade,
    type: 'liquidated',
    value,
  }
}

function getCollateralEventInfo(
  e: DepositCollateralEvent | WithdrawCollateralEvent,
  events: ActivityEvent[]
) {
  //check if collateral changed for borrow or trade
  const tradeOrBorrow = events.find(
    (current) =>
      current.user === e.user &&
      (current instanceof BorrowEvent || current instanceof TradeEvent) &&
      e.loanId === current.loanId
  )

  const { asset, chainId, loanId } = e

  const operation = tradeOrBorrow
    ? tradeOrBorrow instanceof TradeEvent
      ? Operation.Trade
      : Operation.Borrow
    : undefined
  const { decimals } = assetUtils.getChainDetails(chainId, asset)
  const value = e.amount.div(10 ** decimals)
  const type = e instanceof DepositCollateralEvent ? 'collateral deposited' : 'collateral withdrawn'
  return {
    asset,
    operation,
    type,
    value,
    loanId,
  }
}

function getStakeEventInfo(e: StakeEvent | UnstakeEvent) {
  const { token: asset } = e
  const { decimals } = assetUtils.getChainDetails(1, asset)
  const value = e.amount.div(10 ** decimals)

  return {
    type: e instanceof StakeEvent ? 'staking' : 'unstake',
    asset,
    operation: Operation.Stake,
    value,
  }
}

function getActivityInfo(events: ActivityEvent[]) {
  const activity: IActivityInfo[] = []
  events.forEach(async (e) => {
    const { chainId, user, timestamp, txHash } = e
    const info =
      e instanceof TradeEvent || e instanceof CloseWithSwapEvent
        ? getTradeEventInfo(e)
        : e instanceof LiquidationEvent
        ? getLiquidationEventInfo(e)
        : e instanceof DepositCollateralEvent || e instanceof WithdrawCollateralEvent
        ? getCollateralEventInfo(e, events)
        : e instanceof BorrowEvent || e instanceof CloseWithDepositEvent
        ? getBorrowEventInfo(e)
        : e instanceof MintEvent || e instanceof BurnEvent
        ? getLendEventInfo(e)
        : e instanceof StakeEvent || e instanceof UnstakeEvent
        ? getStakeEventInfo(e)
        : {}
    activity.push({ chainId, user, timestamp, txHash, ...info } as IActivityInfo)
  })

  return activity
}

/** Get latest events
 * limit by 3 month to avoid a big response*/
async function getLatestProtocolEvents(chainId: ChainId, monthCount = 3) {
  const opts = { oldGraph: false, monthCount: monthCount }
  const events = await subgraphHistoryUtils.getChainHistory(chainId, undefined, opts)
  const activityInfo = getActivityInfo(events)
  // get loans to identify is it Trade collateral or Borrow
  const loans = activityInfo.reduce((loanIds, info) => {
    if (info.loanId && !info.operation) {
      loanIds.push(info.loanId)
    }
    return loanIds
  }, [] as string[])
  const tradeLoans = await subgraphHistoryUtils.getTradeLoans(chainId, loans)
  return activityInfo.map((info) => {
    if (!info.operation && info.loanId) {
      info.operation = tradeLoans.find((loan: string) => loan === info.loanId)
        ? Operation.Trade
        : Operation.Borrow
    }
    return info
  })
}

async function getUserProtocolEvents(account: string) {
  const opts = { oldGraph: false }

  let result: Array<IActivityInfo> = []
  for (const chainId of supportedChains) {
    const events = await subgraphHistoryUtils.getChainHistory(chainId, account, opts)
    const info = getActivityInfo(events)
    result = result.concat(info)
  }
  return result.sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
}

function openInpersonate(e: IActivityInfo) {
  const link =
    e.operation === Operation.Trade
      ? '/trade/open-positions'
      : e.operation === Operation.Borrow
      ? '/borrow/user-loans'
      : e.operation === Operation.Lend
      ? '/lend/yields'
      : e.operation === Operation.Stake
      ? '/stake'
      : '/'

  providerUtils.setLocalstorageItem('impersonateAddress', e.user)
  switchNetwork(e.chainId)
  window.open(link, '_blank', 'noopener,noreferrer')
  // clean impersonateAddress after 10s
  setTimeout(() => {
    providerUtils.setLocalstorageItem('impersonateAddress', '')
  }, 10000)
}

export default {
  getActivityInfo,
  getChainStats,
  getChainStakedTokenReserve,
  getLatestProtocolEvents,
  getReserveDetails,
  getStakedTokensFromCRV,
  getUserProtocolEvents,
  openInpersonate,
}
